<template>
    <vx-table-row>
        <vx-table-row-title class="break-words">
            {{ props.target.name }}
        </vx-table-row-title>

        <vx-table-row-item class="min-w-[100px]">
            <template v-slot:label> Type: </template>
            <vx-badge v-if="props.target.type === 'ou'" size="xs" color="violet" data-test="type-badge">
                <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                OU
            </vx-badge>
            <vx-badge v-else size="xs" color="slate" data-test="type-badge">
                <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                Group
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item class="min-w-[220px]">
            <template v-slot:label> Role: </template>
            {{ roleLabel }}
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button data-test="remove" @click="confirmRemove" type="button" color="muted" outline class="w-[45px]">
                <font-awesome-icon widthAuto :icon="faCircleMinus" />
            </vx-button>
            <vx-button :href="targetUrl" data-test="go-to-target" color="muted" outline>
                Go To Target <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script lang="ts" setup>
import { faBuildingFlag, faCircleMinus, faGrid2Plus, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { orgUnitInvitationsClient, groupInvitationsClient } from '~/services';
import { ORG_UNIT_USER_ROLES, GROUP_USER_ROLES } from '~/constants/roles';
import { throwSwalRequestError } from '~/utils/request';

export interface InviteTarget {
    type: 'ou' | 'group';
    targetId: string | number;
    name: string;
    role: string;
}

const props = defineProps<{
    teamId: number;
    inviteId: number;
    target: InviteTarget;
    isKiosk?: boolean;
}>();

const emit = defineEmits<{
    removed: [target: InviteTarget];
}>();

const toasted = useToasted();

const rolesMap = computed(() => (props.target.type === 'ou' ? ORG_UNIT_USER_ROLES : GROUP_USER_ROLES));

const roleLabel = computed(() => (rolesMap.value as Record<string, string>)[props.target.role] || props.target.role);

const targetUrl = computed(() => {
    const prefix = props.isKiosk ? '/kiosk' : '/settings';
    if (props.target.type === 'ou') {
        return `${prefix}/teams/${props.teamId}/org-units/${props.target.targetId}`;
    }
    return `${prefix}/teams/${props.teamId}/groups/${props.target.targetId}`;
});

const confirmRemove = () => {
    return VxSwal.confirmation(
        'Remove Invite Target',
        'This target will be removed from the invitation.',
        'danger',
        () => {
            const request =
                props.target.type === 'ou'
                    ? orgUnitInvitationsClient.deleteInvitation(props.teamId, props.target.targetId, props.inviteId)
                    : groupInvitationsClient.deleteInvitation(props.teamId, props.target.targetId, props.inviteId);

            return request
                .then(() => {
                    emit('removed', props.target);
                    toasted.success(`${props.target.name} removed from invite`);
                })
                .catch(throwSwalRequestError);
        }
    );
};
</script>
